import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ecarter,
  marquerVu,
  mesMatchs,
  monLifestyle,
  traits,
  type Match,
  type PageMatchs,
} from '../apiMatching'
import { ExplicationScore, nomAxe } from './Explication'
import { Bouton, Image, LienBouton, Modale, Puce, SqueletteListe } from '../ui'
import { dateHeure, nombre } from '../formats'
import { ErreurApi } from '../api'
import { TEXTES, messageErreur, type Langue } from '../i18n'

/**
 * Le fil « Recommandé pour vous » (spec §7.4).
 *
 * **Trois vides différents, trois messages différents** — règle 10 du §6 de L10. Une liste vide
 * parce que la passe n'a pas encore tourné n'est pas une liste vide parce que rien ne correspond,
 * et aucune des deux n'est un questionnaire non rempli. Les confondre ferait croire à une panne là
 * où il n'y a qu'une nuit à attendre.
 *
 * **L'explication est visible dès la vignette**, pas derrière un clic : « l'explication est ce qui
 * fait cliquer » (spec §7.3). Un score nu ne convainc personne et ne se conteste pas.
 */
export function Recommandations({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]

  const [page, setPage] = useState<PageMatchs | null>(null)
  const [manquants, setManquants] = useState(0)
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(async () => {
    try {
      setPage(await mesMatchs())
    } catch (cause) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [langue])

  useEffect(() => {
    void charger()
  }, [charger])

  /*
    Combien de critères ÉLIMINATOIRES manquent au questionnaire.

    Un nombre et non un booléen : « votre questionnaire est incomplet » se lit comme un reproche
    vague qu'on remet à plus tard, « quatre critères manquent » se lit comme une tâche qui a une
    fin. C'est aussi ce que montre la maquette.

    On ne compte QUE les éliminatoires. Ce sont les seuls dont l'absence change la nature du
    résultat — un critère éliminatoire non renseigné ne filtre rien, donc laisse remonter des
    foyers que l'on aurait écartés. Compter les autres gonflerait le nombre sans que le remplir
    change ce qu'on voit.

    L'échec est silencieux : ce bloc est une aide, et un écran de recommandations qui refuse de
    s'afficher parce qu'un référentiel n'a pas répondu serait une régression pour une facilité.
  */
  useEffect(() => {
    let annule = false

    void Promise.all([traits(langue), monLifestyle()])
      .then(([referentiel, profil]) => {
        if (annule) {
          return
        }

        const repondus = new Set(
          profil.answers
            .filter((reponse) => reponse.selfValue !== null || reponse.toleranceValue !== null)
            .map((reponse) => reponse.traitCode),
        )

        setManquants(
          referentiel.filter((trait) => trait.isEliminatory && !repondus.has(trait.code)).length,
        )
      })
      .catch(() => undefined)

    return () => {
      annule = true
    }
  }, [langue])

  // La date de calcul de la passe : elle est la même pour tous les résultats, donc elle se dit une
  // fois en en-tête plutôt que sur chaque ligne.
  const calculeLe = page?.items[0]?.computedAt ?? null

  const ecarterUn = async (id: string) => {
    try {
      await ecarter(id)
      await charger()
    } catch (cause) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }

  if (erreur !== null) {
    return <p className="erreur">{erreur}</p>
  }

  if (page === null) {
    return <SqueletteListe />
  }

  return (
    <section className="recommandations">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.recommandations}</h2>
          {calculeLe !== null && (
            <span className="tertiaire">
              {t.recommandationsCalculeLe} {dateHeure(calculeLe, langue)}
            </span>
          )}
        </div>
        <p>{t.recommandationsIntro}</p>
      </div>

      {page.items.length === 0 && (
        <p className="socle">
          {/* La passe tourne la nuit : un fil vide au premier jour est le cas NOMINAL, pas une
              panne. Le dire evite le seul ticket que cet ecran produirait sinon. */}
          {t.recommandationsJamaisCalculees}
        </p>
      )}

      {/*
        **Des LIGNES et non des colonnes** — ticket `fo-recommandations-01`, et c'est ce que montre
        la maquette. La grille de vignettes convenait à la recherche, où l'on balaie des photos ;
        ici chaque résultat porte un score, quatre axes et leur explication. Mis en colonne étroite,
        tout cela se replie sur six lignes de texte serré, et l'explication — qui est la raison
        d'être de l'écran (spec §7.3) — devient la première chose qu'on saute.
      */}
      <ul className="pile recommandations__liste">
        {page.items.map((match) => (
          <Vignette key={match.id} match={match} langue={langue} onEcarter={ecarterUn} />
        ))}
      </ul>

      {/*
        **Le questionnaire incomplet se dit ici** — ticket `fo-recommandations-01`, second point.

        C'est le seul écran où l'on constate ce que le questionnaire coûte de ne pas être rempli :
        sans mode de vie, il ne reste que le budget et la proximité, et les résultats se ressemblent
        tous. Le dire ailleurs serait un rappel de plus ; le dire ici, sous les résultats qu'il
        dégrade, c'est une explication.

        En bas et non en haut : quelqu'un qui vient voir ses recommandations vient les voir. Le
        bloc répond à la question qu'il se pose en les lisant — « pourquoi ne me proposent-elles
        que cela ? » — et cette question arrive après.
      */}
      {manquants > 0 && (
        <div className="bloc bloc--accent recommandations__lifestyle">
          <div className="rangee rangee--espace">
            <div>
              <h3>{t.recommandationsLifestyleIncomplet}</h3>
              <p className="secondaire">
                {`${nombre(manquants, langue)} ${
                  manquants > 1 ? t.recommandationsCriteresManquants : t.recommandationsCritereManquant
                } ${t.recommandationsSansEux}`}
              </p>
            </div>
            <LienBouton to="/mode-de-vie">{t.recommandationsCompleter}</LienBouton>
          </div>
        </div>
      )}
    </section>
  )
}

function Vignette({
  match,
  langue,
  onEcarter,
}: {
  match: Match
  langue: Langue
  onEcarter: (id: string) => Promise<void>
}) {
  const t = TEXTES[langue]
  const pourcent = new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 0 })
  const monnaie = new Intl.NumberFormat(langue, { style: 'currency', currency: 'MAD' })

  const [detail, setDetail] = useState(false)

  // Marque vu a l'affichage, une fois. Le compteur de non-lus n'existe pas ici : ce qui compte est
  // que le resultat cesse de remonter comme « nouveau » une fois qu'il a ete montre.
  useEffect(() => {
    if (match.status === 'New') {
      void marquerVu(match.id)
    }
  }, [match.id, match.status])

  const titre = match.listingTitle ?? match.targetDisplayName ?? ''

  return (
    <li className="bloc bloc--serre recommandation">
      <div className="rangee rangee--haut">
        {/* La photo est décorative : le titre est juste à côté, et il est le lien. Un `alt` qui
            le répète serait annoncé deux fois. */}
        <Image
          mediaId={match.coverMediaId}
          alt=""
          ratio="4-3"
          sizes="11rem"
          className="recommandation__media"
          vide={t.annonceSansPhoto}
        />

        <div className="pile pile--serree recommandation__corps">
          <div className="rangee rangee--espace">
            {match.listingId !== null ? (
              <Link to={`/annonces/${match.listingId}`} className="recommandation__titre">
                {titre}
              </Link>
            ) : (
              <span className="recommandation__titre">{titre}</span>
            )}
            <span className="puce puce--accent">{pourcent.format(match.score)}</span>
          </div>

          <p className="secondaire">
            {match.district}
            {match.rentAllInclusive !== null && ` · ${monnaie.format(match.rentAllInclusive)}`}
          </p>

          {/*
            **Une seule barre, et le détail à la demande** — ticket `fo-recommandations-01`,
            réouverture, et c'est ce que montre la maquette.

            Six barres empilées par résultat, sur une liste de six résultats, font trente-six
            barres sur un écran : plus rien ne se compare, parce que tout se ressemble. La barre
            globale est la seule qui serve au balayage — c'est elle qui classe. Les axes restent
            visibles en puces, sans barre : le nom et le chiffre suffisent à repérer celui qui
            tire vers le bas.

            **L'explication n'a pas disparu pour autant** (spec §7.3 : « l'explication est ce qui
            fait cliquer »). Elle est à un clic, entière, dans la même vue qu'avant. Ce qui change
            est qu'on la demande pour UN résultat au lieu de la subir pour six.
          */}
          <p
            className="explication__barre explication__barre--globale"
            role="img"
            aria-label={`${t.recommandationsScore} ${pourcent.format(match.score)}`}
          >
            <span
              className="explication__jauge"
              style={{ inlineSize: `${Math.round(match.score * 100)}%` }}
            />
          </p>

          <div className="rangee rangee--serree recommandation__axes">
            {match.axes.map((axe) => (
              <Puce key={axe.axis} ton={tonAxe(axe.rawScore)}>
                {nomAxe(axe.axis, langue)} {Math.round(axe.rawScore * 100)}
              </Puce>
            ))}
          </div>

          <div className="rangee rangee--serree">
            {match.listingId !== null && (
              <LienBouton to={`/annonces/${match.listingId}`} apparence="secondaire" taille="sm">
                {t.recommandationsVoirAnnonce}
              </LienBouton>
            )}
            <Bouton apparence="secondaire" taille="sm" onClick={() => setDetail(true)}>
              {t.recommandationsDetailScore}
            </Bouton>
            <Bouton apparence="discret" taille="sm" onClick={() => void onEcarter(match.id)}>
              {t.ecarte}
            </Bouton>
          </div>
        </div>
      </div>

      {detail && (
        <Modale titre={t.recommandationsDetailScore} onFermer={() => setDetail(false)}>
          <p className="recommandation__score">{pourcent.format(match.score)}</p>
          <ExplicationScore axes={match.axes} langue={langue} />
          <p className="aide">{t.recommandationsPonderationsAide}</p>
        </Modale>
      )}
    </li>
  )
}

/**
 * La couleur d'une puce d'axe.
 *
 * Trois tons et non six : la puce sert à repérer l'axe qui tire vers le bas, pas à rendre le
 * chiffre une seconde fois. Les seuils sont ceux de la maquette — 90 et 80 — et ne décident de
 * rien d'autre que d'une couleur.
 */
function tonAxe(score: number): 'succes' | 'attention' | undefined {
  if (score >= 0.9) {
    return 'succes'
  }

  return score >= 0.8 ? undefined : 'attention'
}
